<script setup lang="ts">
import { computed } from 'vue';
import type { ServerDisk, ServerResources } from '@atum-remote/protocol/server-inspection';
import { bytes } from '../lib/server-inspection.js';
import {
  type DiskRates,
  diskHealthLabel,
  filesystemPercent,
  healthUnavailableText,
  rate,
  slowLatency,
} from '../lib/server-resources.js';
import UsageBar from './UsageBar.vue';

const props = defineProps<{
  resources: ServerResources;
  rates: ReadonlyMap<string, DiskRates>;
}>();
const kinds: Record<ServerDisk['kind'], string> = {
  nvme: 'NVMe',
  ssd: 'SSD',
  hdd: 'HDD',
  virtual: 'Virtual',
  raid: 'RAID',
  mmc: 'eMMC',
};
const disks = computed(() => props.resources.disks ?? []);
const filesystems = computed(() => props.resources.filesystems ?? []);
const iops = (v: number) => (v >= 100 ? Math.round(v).toLocaleString() : v.toFixed(v ? 1 : 0));
const ms = (v: number | null | undefined) => (v == null ? '—' : `${v.toFixed(v < 10 ? 1 : 0)} ms`);
function healthDetail(disk: ServerDisk) {
  const h = disk.health;
  if (!h) return disk.healthUnavailable ? healthUnavailableText[disk.healthUnavailable] : null;
  const parts: string[] = [];
  if (h.percentUsed != null) parts.push(`${h.percentUsed}% worn`);
  if (h.availableSpare != null) parts.push(`spare ${h.availableSpare}%`);
  if (h.mediaErrors) parts.push(`${h.mediaErrors} media errors`);
  if (h.powerOnHours != null)
    parts.push(`${Math.round(h.powerOnHours).toLocaleString()} h powered on`);
  if (h.dataWrittenBytes != null) parts.push(`${bytes(h.dataWrittenBytes)} written`);
  return parts.join(' · ') || null;
}
function tone(percent: number) {
  return percent >= 95 ? 'danger' : percent >= 90 ? 'warn' : 'ok';
}
</script>

<template>
  <section class="panel res-section" aria-labelledby="res-storage-title">
    <div class="res-section-head">
      <div>
        <h3 id="res-storage-title">Storage</h3>
        <p>
          Throughput, operations and average latency between readings. Drive health comes from SMART
          data published by udisks2 where the host provides it.
        </p>
      </div>
    </div>
    <div v-if="disks.length" class="server-table res-table">
      <table>
        <thead>
          <tr>
            <th>Device</th>
            <th class="num">Read</th>
            <th class="num">Write</th>
            <th class="num" title="Operations per second, read / write">IOPS</th>
            <th class="num" title="Average time per request, read / write">Latency</th>
            <th class="num" title="Share of time with requests in flight">Busy</th>
            <th class="num">Temp.</th>
            <th>Health</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="disk in disks" :key="disk.name">
            <td>
              <span class="res-name"
                ><span class="mono">{{ disk.name }}</span
                ><span class="tag">{{ kinds[disk.kind] }}</span></span
              >
              <span class="res-sub"
                >{{ disk.model ?? 'Unknown model' }} · {{ bytes(disk.sizeBytes) }}</span
              >
            </td>
            <td class="num">{{ rate(rates.get(disk.name)?.readBytes) }}</td>
            <td class="num">{{ rate(rates.get(disk.name)?.writeBytes) }}</td>
            <td class="num">
              <template v-if="rates.get(disk.name)"
                >{{ iops(rates.get(disk.name)!.readIops) }} /
                {{ iops(rates.get(disk.name)!.writeIops) }}</template
              ><template v-else>—</template>
            </td>
            <td
              class="num"
              :data-tone="
                Math.max(
                  rates.get(disk.name)?.readLatency ?? 0,
                  rates.get(disk.name)?.writeLatency ?? 0,
                ) >= slowLatency(disk)
                  ? 'warn'
                  : undefined
              "
            >
              <template v-if="rates.get(disk.name)"
                >{{ ms(rates.get(disk.name)!.readLatency) }} /
                {{ ms(rates.get(disk.name)!.writeLatency) }}</template
              ><template v-else>—</template>
            </td>
            <td class="num">
              {{ rates.get(disk.name) ? `${rates.get(disk.name)!.busy.toFixed(0)}%` : '—' }}
            </td>
            <td class="num" :data-tone="(disk.temperatureC ?? 0) >= 70 ? 'warn' : undefined">
              {{ disk.temperatureC == null ? '—' : `${disk.temperatureC.toFixed(0)} °C` }}
            </td>
            <td>
              <span class="tag res-tone" :data-tone="diskHealthLabel(disk).tone">{{
                diskHealthLabel(disk).label
              }}</span>
              <span v-if="healthDetail(disk)" class="res-sub">{{ healthDetail(disk) }}</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <p v-else-if="!resources.disks" class="res-muted res-gap">
      Per-device throughput and health need a newer ATUM Remote host.
    </p>
    <div v-if="filesystems.length" class="server-table res-table">
      <table>
        <thead>
          <tr>
            <th>Mounted at</th>
            <th>Source</th>
            <th class="res-usage-col">Used</th>
            <th class="num">Free</th>
            <th class="num">Size</th>
            <th class="num" title="Share of file entries (inodes) used">Inodes</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="fs in filesystems" :key="fs.mount">
            <td>
              <span class="res-name"
                ><span class="mono">{{ fs.mount }}</span
                ><span v-if="fs.readOnly" class="tag">read-only</span></span
              >
            </td>
            <td>
              <span class="mono res-sub-inline">{{ fs.source }}</span>
              <span class="res-sub">{{ fs.type }}</span>
            </td>
            <td class="res-usage-col">
              <span class="res-usage">
                <UsageBar
                  :segments="[{ value: filesystemPercent(fs), tone: tone(filesystemPercent(fs)) }]"
                  :total="100"
                  :label="`${fs.mount} ${filesystemPercent(fs).toFixed(0)}% used`"
                />
                <span class="num">{{ filesystemPercent(fs).toFixed(0) }}%</span>
              </span>
            </td>
            <td class="num">{{ bytes(fs.available) }}</td>
            <td class="num">{{ bytes(fs.total) }}</td>
            <td class="num">
              {{
                fs.inodesTotal ? `${(100 * (1 - fs.inodesFree / fs.inodesTotal)).toFixed(0)}%` : '—'
              }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>
